import React, { useEffect } from "react";
import NavBar from "../components/NavBar.jsx";
import { LanguageProvider } from "../lib/paymentCheck/i18n.jsx";
import AudienceSection from "../components/paymentCheck/AudienceSection.jsx";
import ProtectionSection from "../components/paymentCheck/ProtectionSection.jsx";
import RiskCarousel from "../components/paymentCheck/RiskCarousel.jsx";
import "../styles/payment-check.css";

export default function PaymentService({ onNavigate }) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Check a Payment | AskMax Security";
    return () => { document.title = previousTitle; };
  }, []);

  const requestEnquiry = () => onNavigate("/bespoke", {
    enquiry: { service: "check_a_payment", message: "Check a Payment enquiry" },
  });

  return (
    <LanguageProvider>
      <NavBar mode="payment" onNavigate={onNavigate} onSelectService={requestEnquiry} />
      <div className="pc-scope">
        <div className="pc-preview-status" role="status">
          <strong>Information and enquiry only</strong>
          <span>No payment is taken. This page does not analyse a payment, create a case or upload files. Scope and timing are agreed before any review begins.</span>
        </div>
        <main>
          <section className="pc-hero pc-zone--black">
            <div className="pc-container pc-hero__layout">
              <div className="pc-hero__copy">
                <p className="pc-kicker">Get another pair of eyes before you pay</p>
                <h1 className="pc-hero__amount">Check a Payment</h1>
                <p className="pc-hero__question">A payment request has changed?</p>
                <p className="pc-hero__proposition">Are the new details really from the right person?</p>
                <p className="pc-hero__supporting">A service intended to help businesses identify warning signs in an unusual or potentially fraudulent payment request. An enquiry is the first step, not an assessment or a promise that a payment is safe.</p>
              </div>
              <div className="pc-hero__action">
                <p className="pc-hero__action-label">Start with an enquiry</p>
                <p className="pc-hero__action-copy">Our team will review your enquiry, agree the scope of work and explain the next steps before any assessment begins.</p>
                <div className="pc-hero__ctas">
                  <button type="button" className="pc-btn pc-btn--yellow" onClick={requestEnquiry}>Enquire about Check a Payment</button>
                  <a className="pc-btn pc-btn--ghost-dark" href="#payment-help">How AskMax could help</a>
                </div>
              </div>
            </div>
          </section>
          <section className="pc-authority pc-zone--white">
            <div className="pc-container">
              <h2 className="pc-section-title">When might you need it?</h2>
              <ul className="pc-authority__list">
                <li>A supplier suddenly changes their bank details.</li>
                <li>An unexpected invoice arrives.</li>
                <li>Someone asks for an urgent transfer.</li>
                <li>A payment request appears to come from a colleague or trusted business.</li>
                <li>Something about a transaction does not feel right.</li>
              </ul>
            </div>
          </section>
          <section className="pc-continuity pc-zone--black">
            <div className="pc-container pc-reading">
              <h2 className="pc-statement pc-statement--lg">Why does it matter?</h2>
              <p className="pc-lede">Payment fraud can be convincing. Familiar names, convincing invoices and urgent instructions can deceive even experienced business owners.</p>
              <p className="pc-lede pc-lede--muted">Before sending money, independently verify new or changed payment instructions using a number or contact method you already trust. Do not rely on the contact details in the suspicious request.</p>
              <p className="pc-continuity__guardrail">If money has already moved, contact your bank or payment provider immediately. Do not wait for AskMax. Recovery can never be guaranteed.</p>
            </div>
          </section>
          <AudienceSection />
          <ProtectionSection />
          <section className="pc-risk pc-zone--black-deep">
            <div className="pc-container pc-container--wide"><h2 className="pc-kicker">Why this matters</h2></div>
            <RiskCarousel onGetItChecked={requestEnquiry} ctaLabel="Enquire about Check a Payment" />
          </section>
          <section className="pc-how pc-zone--white" id="payment-help">
            <div className="pc-container">
              <h2 className="pc-section-title">How could AskMax help?</h2>
              <div className="pc-how__grid">
                <article className="pc-how__step"><span className="pc-how__num">01</span><h3>Explain the concern</h3><p>Make an initial enquiry without sending passwords, banking credentials, security codes or financial documents.</p></article>
                <article className="pc-how__step"><span className="pc-how__num">02</span><h3>Agree scope and timing</h3><p>Our team will review your enquiry, agree the scope of work and explain the next steps before any assessment begins.</p></article>
                <article className="pc-how__step"><span className="pc-how__num">03</span><h3>Understand the warning signs</h3><p>The intended service is a human review of the agreed information, explaining concerns, uncertainty and practical next steps. It cannot guarantee that a payment is safe.</p></article>
              </div>
              <p className="pc-lede">Automated analysis, case creation, evidence uploads and payment processing are not connected to this website. Any human review is agreed separately after the enquiry.</p>
            </div>
          </section>
          <section className="pc-final pc-zone--black">
            <div className="pc-container pc-reading">
              <h2 className="pc-statement pc-statement--lg">Something does not feel right?</h2>
              <p className="pc-lede pc-lede--muted">Pause and verify the instructions independently. Ask about whether a scoped review could help; no work is booked and no payment is taken by making an enquiry.</p>
              <button type="button" className="pc-btn pc-btn--yellow" onClick={requestEnquiry}>Enquire about Check a Payment</button>
            </div>
          </section>
        </main>
        <footer className="pc-footer pc-zone--white"><div className="pc-container"><p>AskMax Security / Check a Payment</p><p><a href="/legal-notice" onClick={(event) => { event.preventDefault(); onNavigate("/legal-notice"); }}>Legal Notice</a> · <a href="/privacy/" onClick={(event) => { event.preventDefault(); onNavigate("/privacy"); }}>Privacy Notice</a></p><p>Information and enquiry only. No payment or automated payment verification is provided on this page.</p></div></footer>
      </div>
    </LanguageProvider>
  );
}